import React, { useState } from 'react';
import Modal from '@common/modal';
import CloseButton from '@common/closeButton';
import ProgressBar from '@components/progressBar';
import Step1 from '@components/profile/modals/nhisCareerModal/step1';
import Step2 from '@components/profile/modals/nhisCareerModal/step2';
import Step3 from '@components/profile/modals/nhisCareerModal/step3';
import Step4 from '@components/profile/modals/nhisCareerModal/step4';
import Step5 from '@components/profile/modals/nhisCareerModal/step5';
import Step6 from '@components/profile/modals/nhisCareerModal/step6';
import Step7 from '@components/profile/modals/nhisCareerModal/step7';
import NhisCompanyReportHistoryDto from '@domain/rs/nhis/NhisCompanyReportHistoryDto';
import { NhisProviderType } from '@domain/constant/nhis/NHISProviderType';
import useUpdateEffect from '@utils/hooks/useUpdateEffect';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

export enum NHISStep {
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
  Step6,
  Step7,
}

interface NHISCareerModalProps {
  visible: boolean;
  onClose: () => void;
}

const NHISCareerModal = ({ visible, onClose }: NHISCareerModalProps) => {
  const [step, setStep] = useState<NHISStep>(NHISStep.Step1);
  const [provider, setProvider] = useState<NhisProviderType | null>(null); // Step2에서 선택한 provider
  const [selectedCompanies, setSelectedCompanies] = useState<NhisCompanyReportHistoryDto[]>([]); // Step5에서 선택한 companies
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  useUpdateEffect(() => {
    setStep(NHISStep.Step1);
  }, [visible]);

  const getContentEl = () => {
    switch (step) {
      case NHISStep.Step1:
        return <Step1 nextStep={nextStep} />;
      case NHISStep.Step2:
        return <Step2 prevStep={prevStep} nextStep={nextStep} setProvider={setProvider} />;
      case NHISStep.Step3:
        return <Step3 prevStep={prevStep} nextStep={nextStep} provider={provider} />;
      case NHISStep.Step4:
        return <Step4 nextStep={nextStep} setSelectedCompanies={setSelectedCompanies} />;
      case NHISStep.Step5:
        return (
          <Step5
            nextStep={nextStep}
            onClose={onClose}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
          />
        );
      case NHISStep.Step6:
        return <Step6 prevStep={prevStep} nextStep={nextStep} selectedCompanies={selectedCompanies} />;
      case NHISStep.Step7:
        return <Step7 onClose={onClose} selectedCompanies={selectedCompanies} />;
      default:
        return null;
    }
  };

  const getProgress = () => {
    switch (step) {
      case NHISStep.Step1:
        return 20;
      case NHISStep.Step2:
        return 40;
      case NHISStep.Step3:
        return 40;
      case NHISStep.Step4:
        return 60;
      case NHISStep.Step5:
        return 60;
      case NHISStep.Step6:
        return 80;
      case NHISStep.Step7:
        return 100;
      default:
        return 0;
    }
  };

  if (!visible) return null;

  return (
    <Modal visible={visible} dimmed={true}>
      <div className={cx('modalArea')}>
        <h2 className={cx('title')}>경력 불러오기</h2>
        {getContentEl()}
        {step !== NHISStep.Step4 && <ProgressBar className={cx('progressBar')} progress={getProgress()} />}
        <CloseButton className={cx('btnClose')} onClick={onClose} />
      </div>
    </Modal>
  );
};

export default NHISCareerModal;
